import React, { useEffect } from 'react';
import { Nav, NavDropdown } from 'react-bootstrap';
import { MapDispatchToProps, connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { bindActionCreators } from 'redux';
import { clearSelectedAccount } from '../../actions/accountActions';
import { ensureSelectedCompany } from '../../actions/companyActions';
import { ensureUserRoles } from '../../actions/userRoleActions';
import { Loadable } from '../../models';
import { AccountDetail, PageParameters } from '../../models/account';
import { Company } from '../../models/company';
import { AppState } from '../../models/state/appState';
import { AspNetRole, Role, hasAspNetRole, hasRole } from '../../models/state/enumerations/Role';
import { UserRoles } from '../../models/state/userRolesState';
import { UserSetting } from '../../models/state/userSettings';
import { getUserSetting } from '../../services/helpers';

interface DispatchToProps {
  actions: {
    clearSelectedAccount(): void;
    ensureSelectedCompany(id: number): void;
    ensureUserRoles(value: PageParameters): void;
  };
}

interface OwnProps {
  companyId: number;
  accountId?: number;
  onNavClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface StateToProps {
  account: Loadable<AccountDetail>;
  canConfigAlertManager: boolean;
  canConfigSmsAlerts: boolean;
  canManagePortals?: boolean;
  company: Loadable<Company>;
  hasLimitedAccess: boolean;
  hasPortingWizardAccess: boolean;
  hasSpendCapAccess: boolean;
  userRoles?: Loadable<UserRoles>;
  hasSimOrderingAccess: boolean;
  userSettings: Array<UserSetting>;
}

type Props = OwnProps & DispatchToProps & StateToProps;

function CompanyTopNav({
  actions,
  accountId,
  companyId,
  canConfigAlertManager,
  canConfigSmsAlerts,
  hasLimitedAccess,
  hasPortingWizardAccess,
  hasSpendCapAccess,
  hasSimOrderingAccess,
  canManagePortals,
  userSettings,
  onNavClick
}: Props) {
  useEffect(() => {
    actions.ensureSelectedCompany(companyId);
    actions.ensureUserRoles({ companyId, accountId });
  }, [accountId, companyId]);

  const companyUrl = `/company/${companyId}`;
  const baseUrl = accountId ? `${companyUrl}/account/${accountId}` : companyUrl;
  const linkProps = { onClick: onNavClick, className: 'nav-item' };
  const hideDashboard = getUserSetting('HideDashboard', userSettings)?.settingValue?.toLowerCase() === 'true';

  return (
    <Nav>
      {!hideDashboard && (
        <LinkContainer to={`${baseUrl}/dashboard`} {...linkProps}>
          <a aria-label="Dashboard">
            <span>
              <i className="fa-duotone fa-monitor-waveform" />
              <span className="nav-label">Dashboard</span>
            </span>
          </a>
        </LinkContainer>
      )}
      <LinkContainer to={`${baseUrl}/sim`} {...linkProps}>
        <a aria-label="SIM Cards">
          <span>
            <i className="fa-duotone fa-sim-card" />
            <span className="nav-label">SIM Cards</span>
          </span>
        </a>
      </LinkContainer>
      <LinkContainer to={`${baseUrl}/sim/submission`} {...linkProps}>
        <a aria-label="Submissions">
          <span>
            <i className="fa-duotone fa-paper-plane" />
            <span className="nav-label">Submissions</span>
          </span>
        </a>
      </LinkContainer>
      {hasSpendCapAccess && (
        <LinkContainer to={`${baseUrl}/spendcap`} {...linkProps}>
          <a>
            <span>
              <i className="fa-duotone fa-arrow-up-to-line" />
              <span className="nav-label">Spend Caps</span>
            </span>
          </a>
        </LinkContainer>
      )}
      {hasSimOrderingAccess && (
        <LinkContainer to={`${baseUrl}/simorders`} {...linkProps}>
          <a>
            <span>
              <i className="fa-duotone fa-clipboard-list" />
              <span className="nav-label">SIM Orders</span>
            </span>
          </a>
        </LinkContainer>
      )}
      {!hasLimitedAccess && hasPortingWizardAccess && (
        <LinkContainer to={`${baseUrl}/porting-migration`} {...linkProps}>
          <a>
            <span>
              <i className="fa-duotone fa-arrow-right-to-arc" />
              <span className="nav-label">Porting &amp; Migration</span>
            </span>
          </a>
        </LinkContainer>
      )}
      {(canConfigSmsAlerts || canManagePortals || canConfigAlertManager) && (
        <NavDropdown
          id="nav-dropdown"
          title={
            <span>
              <i className="fa-duotone fa-screwdriver-wrench" />
              <span className="nav-label">Admin</span>
            </span>
          }
        >
          {canConfigAlertManager && (
            <LinkContainer to={`${baseUrl}/alertManagerDefinitions`} {...linkProps}>
              <NavDropdown.Item title="Alert Manager Definitions">
                <span>
                  <i className="fa-solid fa-bell" />
                  Alert Manager Definitions
                </span>
              </NavDropdown.Item>
            </LinkContainer>
          )}
          {canConfigSmsAlerts && (
            <LinkContainer to={`${baseUrl}/smsalert`} {...linkProps}>
              <NavDropdown.Item title="SMS Alert Configuration">
                <span>
                  <i className="fa-solid fa-sms" />
                  SMS Alert Configuration
                </span>
              </NavDropdown.Item>
            </LinkContainer>
          )}
          {canManagePortals && (
            <LinkContainer to={`${baseUrl}/portalManagement/`} {...linkProps}>
              <NavDropdown.Item title="Portal Administration">
                <span>
                  <i className="fa-solid fa-tools" />
                  Portal Administration
                </span>
              </NavDropdown.Item>
            </LinkContainer>
          )}
        </NavDropdown>
      )}
    </Nav>
  );
}

const mapStateToProps = (state: AppState, ownProps?: OwnProps): StateToProps => {
  const { account, company } = state.filter;
  const { userSettings } = state.userSettings;
  const { user } = state.loginDetails;
  const isAtlasAccount = (account && account.isAtlasAccount) === true;
  const portalCapable = (account && account.portalCapable) === true;
  const userRoles =
    ownProps && state.userRoles.companyId === ownProps.companyId && state.userRoles.accountId === ownProps.accountId
      ? state.userRoles
      : undefined;
  const isCellhireUser = (user && user.isCellhireUser) || false;
  const { companyStatus } = company;

  return {
    account,
    company,
    userSettings: userSettings || [],
    canConfigSmsAlerts: isAtlasAccount === true && hasRole(userRoles, [Role.SmsAlertsConfig]),
    canConfigAlertManager: hasRole(userRoles, [Role.AssignAlertManager]) && (!ownProps?.accountId || !!account.id),
    canManagePortals: !!user && hasAspNetRole(user.user, AspNetRole.PortalAdmin) && (!ownProps?.accountId || portalCapable),
    hasLimitedAccess: !isCellhireUser && !!(companyStatus && companyStatus.limitedAccess),
    hasPortingWizardAccess: hasRole(userRoles, [Role.PortingWizard]) && (!ownProps?.accountId || isAtlasAccount),
    hasSpendCapAccess: hasRole(userRoles, [Role.SpendCapAdmin]),
    hasSimOrderingAccess: hasRole(userRoles, [Role.SimOrdering])
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchToProps, any> = (dispatch) => ({
  actions: bindActionCreators({ clearSelectedAccount, ensureSelectedCompany, ensureUserRoles }, dispatch)
});

export default connect<StateToProps, DispatchToProps, OwnProps, AppState>(
  mapStateToProps,
  mapDispatchToProps
)(CompanyTopNav);
